'use client';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView } from 'motion/react';
import { cn } from '@/lib/utils';

interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  circleClassName?: string;
  autoAnimate?: boolean;
  defaultOpacity?: boolean;
  animationDelay?: number;
  animationDuration?: number;
  triggerOnce?: boolean;
  threshold?: number;
  disableInView?: boolean;
}

const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  className,
  circleClassName,
  animationDelay = 0,
  animationDuration = 0.6,
  triggerOnce = false,
  disableInView = false,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const isInView = useInView(cardRef, {
    once: triggerOnce,
    margin: "0px 0px -100px 0px"
  });

  const visibleState = { opacity: 1, y: 0 };
  const hiddenState = { opacity: 0, y: 30 };

  // Throttled mouse move via rAF — only on non-touch devices
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isTouchDevice || !cardRef.current) return;
    if (rafId.current) return; // skip if a frame is already queued
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    });
  }, [isTouchDevice]);

  const handleMouseEnter = useCallback(() => {
    if (!isTouchDevice) setIsHovered(true);
  }, [isTouchDevice]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  }, []);

  return (
    <motion.div
      ref={cardRef}
      className={cn("relative overflow-hidden isolate group", className)}
      initial={disableInView ? false : hiddenState}
      animate={disableInView ? visibleState : isInView ? visibleState : hiddenState}
      transition={{
        duration: animationDuration,
        delay: animationDelay,
        ease: "easeOut" as const
      }}
      onMouseMove={isTouchDevice ? undefined : handleMouseMove}
      onMouseEnter={isTouchDevice ? undefined : handleMouseEnter}
      onMouseLeave={isTouchDevice ? undefined : handleMouseLeave}
    >
      {children}

      {/* Cursor glow effect — desktop only */}
      {!isTouchDevice && (
        <motion.div
          className={cn(
            "absolute inset-0 pointer-events-none z-20",
            circleClassName
          )}
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute w-[150px] h-[150px] bg-theme rounded-full blur-[90px]"
            style={{ left: mousePosition.x - 150, top: mousePosition.y - 150 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={isHovered ? { scale: 1, opacity: 0.3 } : { scale: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
          <motion.div
            className="absolute w-[200px] h-[200px] bg-theme/60 rounded-full blur-[40px]"
            style={{ left: mousePosition.x - 100, top: mousePosition.y - 100 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={isHovered ? { scale: 1.2, opacity: 0.2 } : { scale: 0, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          />
          <motion.div
            className="absolute w-[100px] h-[100px] bg-theme/40 rounded-full blur-[20px]"
            style={{ left: mousePosition.x - 50, top: mousePosition.y - 50 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={isHovered ? { scale: 1.5, opacity: 0.1 } : { scale: 0, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          />
        </motion.div>
      )}

      {/* Subtle content scale — desktop only */}
      {!isTouchDevice && (
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none"
          animate={isHovered ? { scale: 1.01 } : { scale: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      )}
    </motion.div>
  );
};

export default AnimatedCard;
